import { useState, type ReactNode } from 'react'
import { ShellHeaderActions } from "@/components/layout/ShellHeader"
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ReportSearch, ReportVerdict } from '@/components/shared/analytics/AnalyticsReport'
import { cn } from '@/lib/utils'
import { privatize } from '@/lib/utils/revenueUtils'
import type { RevenueTransaction } from '@/lib/utils/revenueUtils'
import type { AdsAttributionData } from './hooks/useAdsAttribution'
import type { ChartDataPoint, LandingPageRow, SourceRow } from './hooks/useAttributionData'
import AttributionCampaigns from './AttributionCampaigns'
import SourcesTable from './SourcesTable'
import LandingPagesTable from './LandingPagesTable'
import type { GoalType } from './AttributionCampaigns'
import RecentTransactionsPanel from './RecentTransactionsPanel'
import SpendEarnedChart from './SpendEarnedChart'
import SourcesRevenueTable from './SourcesRevenueTable'
import {
  CalloutCard,
  GoogleAdsMark,
  MoneyTile,
  MoneyTiles,
  NeedsRevenueValue,
  StripeMark,
  TipRow,
  WarningBanner,
} from './AttributionParts'
import { fmtCount, fmtMoney, type SourceRevenueRow } from './attributionView'
import { SegmentedControl } from '@/components/shared/layout'
import { renewalsByCampaign } from './collectedSplit'

/**
 * Attribution reports (Paper "03 - App · Attribution"): the headline, the
 * money card, the tables and the step strip, per connection state. All
 * presentational: `AdsAttributionDashboard` reads the hooks and passes props,
 * the DEV states gallery passes Paper's mock values.
 */

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function ReportHeader({ title, meta, actions }: { title: ReactNode; meta?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <ReportVerdict meta={meta}>
        <span>{title}</span>
      </ReportVerdict>
      {actions && <div className="flex shrink-0 items-center gap-2 empty:hidden">{actions}</div>}
    </div>
  )
}

/**
 * "Hide amounts" (privacy mode, remembered per browser) and Connections. Inside
 * the app shell they sit in the header bar next to the date range (Paper);
 * elsewhere (gallery, share mode) they render beside the headline.
 */
export function ReportActions({
  privacyMode,
  onTogglePrivacy,
  onConnections,
}: {
  privacyMode?: boolean
  onTogglePrivacy?: () => void
  onConnections?: () => void
}) {
  return (
    <ShellHeaderActions>
      {onTogglePrivacy && (
        <Button variant="outline" size="sm" onClick={onTogglePrivacy} aria-pressed={privacyMode} className="h-8 px-2.5 text-xs">
          {privacyMode ? 'Show amounts' : 'Hide amounts'}
        </Button>
      )}
      {onConnections && (
        <Button variant="outline" size="sm" onClick={onConnections} className="h-8 px-2.5 text-xs">
          Connections
        </Button>
      )}
    </ShellHeaderActions>
  )
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

/** Paper "Google Ads needs reconnecting": amber, Meta keeps reporting. */
export function GoogleAdsReconnectBanner({
  health,
  otherPlatformsOk,
  onReconnect,
}: {
  health: 'stale' | 'expired'
  otherPlatformsOk: boolean
  onReconnect: () => void
}) {
  const body = health === 'expired'
    ? `Spend from Google shows as unavailable until you reconnect.${otherPlatformsOk ? ' Meta is up to date.' : ''}`
    : `Google spend hasn't synced in over a day, so it may be behind.${otherPlatformsOk ? ' Meta is up to date.' : ''}`
  return (
    <WarningBanner
      inline
      icon={<GoogleAdsMark />}
      title="Google Ads needs reconnecting"
      body={body}
      action={
        <Button variant="outline" size="sm" className="h-7 px-[11px] text-xs" onClick={onReconnect}>
          Reconnect
        </Button>
      }
    />
  )
}

/**
 * Paper "Attribution · Stripe disconnected": amber, not red. Not wired: the
 * API exposes no Stripe health, last-synced time or error date yet.
 */
export function StripeDisconnectedBanner({
  since,
  reason,
  keyMasked,
  onReconnect,
}: {
  /** "Sep 23" */
  since: string
  reason: string
  keyMasked?: string | null
  onReconnect: () => void
}) {
  return (
    <WarningBanner
      icon={<StripeMark />}
      title={`Stripe stopped sharing payments on ${since}`}
      body={`${reason} Earned and return below stop at ${since} until you reconnect.`}
      aside={keyMasked ? `Key ${keyMasked}` : null}
      action={
        <Button size="sm" className="h-8 px-3.5 text-[13px]" onClick={onReconnect}>
          Reconnect Stripe
        </Button>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Tables card: Campaigns · Landing pages · Sources
// ---------------------------------------------------------------------------

type TabType = 'campaigns' | 'sources' | 'pages'

export function AttributionTablesCard({
  showCampaigns,
  isLoadingAds = false,
  adsAttributionData,
  adsAttributionError = null,
  sourceData,
  landingPages,
  connectorType,
  transactions,
  basePath,
  navigate,
  privacyMode = false,
  needsRevenue = null,
  goal,
  landingPageGoals = {},
}: {
  showCampaigns: boolean
  isLoadingAds?: boolean
  adsAttributionData: AdsAttributionData | null
  adsAttributionError?: string | null
  sourceData: SourceRow[]
  landingPages: LandingPageRow[]
  connectorType: 'stripe' | 'foxy' | null
  transactions: RevenueTransaction[]
  basePath: string
  navigate: (path: string) => void
  privacyMode?: boolean
  needsRevenue?: string | null
  goal?: GoalType
  landingPageGoals?: Record<string, { trials: number; form_submissions: number }>
}) {
  const [tab, setTab] = useState<TabType>(showCampaigns ? 'campaigns' : 'sources')
  const [searchTerm, setSearchTerm] = useState('')
  const current: TabType = tab === 'campaigns' && !showCampaigns ? 'sources' : tab
  const tabs: Array<{ id: TabType; label: string }> = [
    ...(showCampaigns ? [{ id: 'campaigns' as const, label: 'Campaigns' }] : []),
    { id: 'pages', label: 'Landing pages' },
    { id: 'sources', label: 'Sources' },
  ]
  const title = tabs.find((t) => t.id === current)?.label

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-ui-hairline px-[18px] py-3.5">
        <h3 className="text-sm font-medium leading-[18px] text-foreground">{title}</h3>
        <span className="flex-1" />
        {current !== 'campaigns' && (
          <ReportSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={`Search ${current === 'sources' ? 'sources' : 'pages'}`}
            className="sm:w-[200px]"
          />
        )}
        <div role="tablist" aria-label="Breakdown" className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={current === t.id}
              onClick={() => { setTab(t.id); setSearchTerm('') }}
              className={cn(
                'rounded-md px-[9px] py-1 text-xs leading-4 transition-colors',
                current === t.id
                  ? 'bg-card font-medium text-ui-title shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_1px_2px_rgb(0_0_0/0.04)]'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {current === 'sources' && (
        <SourcesTable sourceData={sourceData} searchTerm={searchTerm} connectorType={connectorType} privacyMode={privacyMode} />
      )}

      {current === 'pages' && (
        <LandingPagesTable landingPages={landingPages} searchTerm={searchTerm} connectorType={connectorType} privacyMode={privacyMode} goal={goal} landingPageGoals={landingPageGoals} />
      )}

      {current === 'campaigns' && (
        <div>
          <div className="px-[18px] py-4">
            <AttributionCampaigns
              adsAttributionData={adsAttributionData}
              isLoading={isLoadingAds}
              error={adsAttributionError}
              privacyMode={privacyMode}
              needsRevenue={needsRevenue}
              renewalsByCampaign={needsRevenue ? null : renewalsByCampaign(transactions)}
            />
          </div>
          {!needsRevenue && <RecentTransactionsPanel transactions={transactions} privacyMode={privacyMode} />}
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Stripe only, no ads (Paper "Attribution · Stripe only, no ads · 1440")
// ---------------------------------------------------------------------------

export function StripeOnlyReport({
  provider,
  title,
  meta,
  earned,
  customers,
  trials,
  chartData,
  sources,
  funnel,
  privacyMode = false,
  onConnectAds,
  actions,
  today,
}: {
  provider: string
  title: string
  meta?: string | null
  earned: number
  /** "Paying customers" when payments carry a customer id, else "Payments" */
  customers: { label: string; value: number }
  trials: number
  chartData: ChartDataPoint[]
  sources: SourceRevenueRow[]
  /** "From visit to payment" (AttributionFunnel) */
  funnel: ReactNode
  privacyMode?: boolean
  onConnectAds: () => void
  actions?: ReactNode
  today?: string
}) {
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)
  return (
    <div className="flex w-full flex-col gap-4">
      <ReportHeader title={title} meta={meta} actions={actions} />
      <TipRow
        lead="Running ads?"
        body="Connect Meta or Google Ads and this page adds spend and return per $1 for each campaign."
        action={
          <Button variant="outline" size="sm" className="h-7 px-3 text-xs" onClick={onConnectAds}>
            Connect ads
          </Button>
        }
      />
      <div className="well flex flex-col gap-2">
        <Card className="gap-0 overflow-hidden py-0">
          <MoneyTiles>
            <MoneyTile first selected label="Earned" value={money(earned)} />
            <MoneyTile label={customers.label} value={privatize(fmtCount(customers.value), privacyMode)} />
            <MoneyTile label="Trials started" value={privatize(fmtCount(trials), privacyMode)} />
          </MoneyTiles>
          <div className="px-5 pt-4 pb-2">
            <SpendEarnedChart
              chartData={chartData}
              series="earned"
              earnedKey="revenue"
              earnedLabel={`Earned (${provider})`}
              spentLabel=""
              privacyMode={privacyMode}
              today={today}
            />
          </div>
        </Card>
        <SourcesRevenueTable rows={sources} totalDollars={earned} privacyMode={privacyMode} />
        {funnel}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ads, no Stripe (Paper "Attribution · Ads, no Stripe · 1440")
// ---------------------------------------------------------------------------

export function AdsNoStripeReport({
  provider,
  platform,
  title,
  meta,
  spend,
  perDay,
  landed,
  chartData,
  tables,
  funnel,
  banner,
  privacyMode = false,
  onConnectRevenue,
  actions,
}: {
  provider: string
  /** "Meta" when one platform carries the spend */
  platform: string | null
  title: string
  meta?: string | null
  spend: number
  perDay: number | null
  landed: number
  chartData: ChartDataPoint[]
  /** The Campaigns · Landing pages · Sources card, in "Needs Stripe" mode */
  tables: ReactNode
  /** "From visit to payment" (AttributionFunnel), in "Needs Stripe" mode */
  funnel: ReactNode
  /** Connector health, under the callout */
  banner?: ReactNode
  privacyMode?: boolean
  onConnectRevenue: () => void
  actions?: ReactNode
}) {
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)
  return (
    <div className="flex w-full flex-col gap-4">
      <ReportHeader title={title} meta={meta} actions={actions} />
      <CalloutCard
        icon={<StripeMark />}
        title={`Connect ${provider} to see what ads earned`}
        body={`Spend and clicks come from ${platform ?? 'your ad accounts'}. Payments come from ${provider}, matched to the ad click that brought each customer in.`}
        aside="Read-only key · pulls 30 days"
        action={
          <Button size="sm" className="h-8 px-3.5 text-[13px]" onClick={onConnectRevenue}>
            Connect {provider}
          </Button>
        }
      />
      {banner}
      <div className="well flex flex-col gap-2">
        <Card className="gap-0 overflow-hidden py-0">
          <MoneyTiles>
            <MoneyTile
              first
              selected
              label="Spent on ads"
              value={money(spend)}
              suffix={perDay != null && perDay > 0 ? `${money(perDay)} a day` : null}
            />
            <MoneyTile label="Landed on your site" value={fmtCount(landed)} />
            <MoneyTile label="Earned and return per $1" value={<NeedsRevenueValue provider={provider} />} />
          </MoneyTiles>
          <div className="px-5 pt-4 pb-2">
            <SpendEarnedChart
              chartData={chartData}
              series="spent"
              earnedKey="revenue"
              earnedLabel=""
              spentLabel={`Spent (${platform ?? 'ads'})`}
              note={`Earned bars appear next to spend once ${provider} is connected`}
              privacyMode={privacyMode}
            />
          </div>
        </Card>
        {tables}
        {funnel}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ads and Stripe (Paper "Attribution · 1440"): money card body
// ---------------------------------------------------------------------------

/** Min conversions behind avg_hours_to_convert to show the tile. */
const MIN_TTC_SAMPLES = 5

export function AdsMoneyCard({
  earned,
  spend,
  roas,
  chartData,
  earnedKey,
  earnedLabel,
  spentLabel,
  privacyMode = false,
  loadingNote,
  today,
  avgHoursToConvert,
  timeToConvertSampleSize,
}: {
  earned: number
  spend: number
  roas: number
  chartData: ChartDataPoint[]
  earnedKey: 'adRevenue' | 'attributedRevenue' | 'revenue'
  earnedLabel: string
  spentLabel: string
  privacyMode?: boolean
  loadingNote?: ReactNode
  today?: string
  avgHoursToConvert?: number | null
  timeToConvertSampleSize?: number
}) {
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)
  const showTtc =
    avgHoursToConvert != null &&
    (timeToConvertSampleSize ?? 0) >= MIN_TTC_SAMPLES
  const ttcDays = showTtc ? avgHoursToConvert! / 24 : null
  const ttcLabel = ttcDays == null ? '—'
    : ttcDays < 1 ? `${Math.round(avgHoursToConvert!)}h`
    : ttcDays < 2 ? `${ttcDays.toFixed(1)}d`
    : `${Math.round(ttcDays)}d`

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <MoneyTiles>
        <MoneyTile first selected label="Earned from ads" value={money(earned)} />
        <MoneyTile label="Spent on ads" value={money(spend)} />
        <MoneyTile label="Return per $1 spent" value={roas > 0 ? privatize(`${roas.toFixed(2)}×`, privacyMode) : '—'} />
        {showTtc && (
          <MoneyTile
            label="Avg. time to convert"
            value={ttcLabel}
            suffix={`${timeToConvertSampleSize} customers`}
          />
        )}
      </MoneyTiles>
      <div className="px-5 pt-4 pb-2">
        {loadingNote}
        <SpendEarnedChart
          chartData={chartData}
          earnedKey={earnedKey}
          earnedLabel={earnedLabel}
          spentLabel={spentLabel}
          privacyMode={privacyMode}
          today={today}
        />
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// AdsHeroCard — replaces AdsMoneyCard in the full ads+revenue view.
// Big verdict number + daily chart + goal selector, all in one card.
// ---------------------------------------------------------------------------

const GOAL_OPTIONS_HERO: { value: GoalType; label: string }[] = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'trials',  label: 'Trials'  },
  { value: 'forms',   label: 'Forms'   },
  { value: 'visits',  label: 'Page visits' },
]

export function AdsHeroCard({
  platform,
  earnedDollars,
  spend,
  roas,
  totalTrialCount,
  adSessions,
  adFormSubmits,
  chartData,
  earnedKey,
  earnedLabel,
  spentLabel,
  provider,
  goal,
  onGoalChange,
  privacyMode = false,
  loadingNote,
  today,
  avgHoursToConvert,
  timeToConvertSampleSize,
  warning,
}: {
  platform: string | null
  earnedDollars: number
  spend: number
  roas: number
  totalTrialCount: number
  adSessions: number
  adFormSubmits: number
  chartData: ChartDataPoint[]
  earnedKey: 'adRevenue' | 'attributedRevenue' | 'revenue'
  earnedLabel: string
  spentLabel: string
  provider: string
  goal: GoalType
  onGoalChange: (g: GoalType) => void
  privacyMode?: boolean
  loadingNote?: ReactNode
  today?: string
  avgHoursToConvert?: number | null
  timeToConvertSampleSize?: number
  /** Inline warning to show at the bottom of the chart area (replaces standalone Alert) */
  warning?: React.ReactNode
}) {
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)
  const MIN_TTC = 5
  const showTtc = avgHoursToConvert != null && (timeToConvertSampleSize ?? 0) >= MIN_TTC
  const ttcDays = showTtc ? avgHoursToConvert! / 24 : null
  const ttcLabel = ttcDays == null ? '—'
    : ttcDays < 1  ? `${Math.round(avgHoursToConvert!)}h`
    : ttcDays < 2  ? `${ttcDays.toFixed(1)}d`
    : `${Math.round(ttcDays)}d`

  // Verdict per goal ──────────────────────────────────────────────────────────
  const verdicts: Record<GoalType, { number: string; label: string; tone: 'positive' | 'warn' | 'neutral' }> = {
    revenue: {
      number: earnedDollars > 0 ? money(earnedDollars) : '$0',
      label:  earnedDollars > 0
        ? `back for every $1 you spent on ${platform ?? 'ads'}`
        : `earned from ${platform ?? 'ad'} clicks`,
      tone: earnedDollars > 0 ? 'positive' : spend > 0 ? 'warn' : 'neutral',
    },
    trials: {
      number: fmtCount(totalTrialCount),
      label:  `trial${totalTrialCount !== 1 ? 's' : ''} started from your ${platform ?? 'ad'} campaigns`,
      tone: totalTrialCount > 0 ? 'positive' : 'neutral',
    },
    forms: {
      number: fmtCount(adFormSubmits),
      label:  `form${adFormSubmits !== 1 ? 's' : ''} submitted from your ${platform ?? 'ad'} campaigns`,
      tone: adFormSubmits > 0 ? 'positive' : 'neutral',
    },
    visits: {
      number: fmtCount(adSessions),
      label:  `people landed from your ${platform ?? 'ad'} campaigns`,
      tone: adSessions > 0 ? 'positive' : 'neutral',
    },
  }
  const v = verdicts[goal]
  const toneClass = v.tone === 'positive' ? 'text-primary-strong' : v.tone === 'warn' ? 'text-warning' : 'text-foreground'

  // Map goal → chart series key and label
  const chartEarnedKey = (
    { revenue: 'adRevenue', trials: 'trials', forms: undefined, visits: 'sessions' } as const
  )[goal]
  const chartEarnedLabel = {
    revenue: earnedLabel,
    trials:  'Trials started',
    forms:   '',
    visits:  'Visits from ads',
  }[goal]

  return (
    <Card className="gap-0 overflow-hidden py-0">
      {/* ── Goal selector — TOP, so the chart below immediately reflects the choice ── */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border/30 bg-muted/30 px-5 py-3">
        <span className="text-xs text-muted-foreground shrink-0">Measure by</span>
        <SegmentedControl<GoalType>
          options={GOAL_OPTIONS_HERO}
          value={goal}
          onChange={onGoalChange}
        />
      </div>

      {/* ── Verdict + chart ── */}
      <div className="px-5 pt-5 pb-4">
        <div className="mb-4">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className={`text-[32px] font-bold tabular-nums leading-none tracking-tight ${toneClass}`}>
              {privacyMode ? '•••' : v.number}
            </span>
            <span className="text-base font-medium leading-none text-foreground">{v.label}</span>
          </div>
          {showTtc && (
            <p className="mt-2 text-xs text-muted-foreground">
              Avg. {ttcLabel} click → payment · based on {timeToConvertSampleSize} customers
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {spend > 0 && (
              <span className="text-muted-foreground">
                Spent <span className="font-medium text-foreground tabular-nums">{money(spend)}</span>
              </span>
            )}
            {roas > 0 && goal === 'revenue' && (
              <span className="text-muted-foreground">
                Return <span className="font-medium text-foreground tabular-nums">{roas.toFixed(2)}×</span>
              </span>
            )}
            {goal === 'trials' && totalTrialCount > 0 && spend > 0 && (
              <span className="text-muted-foreground">
                Cost per trial <span className="font-medium text-foreground tabular-nums">{money(spend / totalTrialCount)}</span>
              </span>
            )}
            {goal === 'forms' && adFormSubmits > 0 && spend > 0 && (
              <span className="text-muted-foreground">
                Cost per form <span className="font-medium text-foreground tabular-nums">{money(spend / adFormSubmits)}</span>
              </span>
            )}
            {goal === 'visits' && adSessions > 0 && spend > 0 && (
              <span className="text-muted-foreground">
                Cost per visit <span className="font-medium text-foreground tabular-nums">{money(spend / adSessions)}</span>
              </span>
            )}
          </div>
        </div>
        {loadingNote}
        <SpendEarnedChart
          chartData={chartData}
          earnedKey={chartEarnedKey ?? earnedKey}
          earnedLabel={chartEarnedLabel}
          spentLabel={spentLabel}
          series={chartEarnedKey == null ? 'spent' : 'both'}
          note={chartEarnedKey == null ? 'Daily form data not available — showing spend only' : undefined}
          otherRevenueLabel={goal === 'revenue' ? 'Other revenue' : null}
          privacyMode={privacyMode}
          today={today}
        />
        {/* Warning consolidated inside the card — no separate Alert card below */}
        {warning && <div className="mt-4">{warning}</div>}
      </div>
    </Card>
  )
}
